import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { getBusinesses } from "services/businessService";
import { Business, PaginatedResponse } from "types";
import Card from "components/card";
import { useToast } from "components/Toast";
import Button from "components/button";

const BusinessDirectory: React.FC = () => {
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [pagination, setPagination] = useState<
    PaginatedResponse<Business>["meta"] | null
  >(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: "",
    category: "",
    village: "",
  });
  const toast = useToast();

  const fetchBusinesses = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        // Only include filters that have values
        const params: any = { page };
        if (filters.search) params.search = filters.search;
        if (filters.category) params.category = filters.category;
        if (filters.village) params.village = filters.village;
        params.status = "approved";

        const res = await getBusinesses(params);
        console.log("Businesses response:", res);

        // Service already returns res.data, which is { data: [...], meta: {...}, links: {...} }
        if (res && Array.isArray(res.data)) {
          setBusinesses(res.data);
          setPagination(res.meta || null);
        } else if (Array.isArray(res)) {
          setBusinesses(res);
          setPagination(null);
        } else {
          setBusinesses([]);
          setPagination(null);
        }
      } catch (err) {
        console.error("Error fetching businesses:", err);
        toast.showError("Failed to load businesses.");
        setBusinesses([]);
        setPagination(null);
      } finally {
        setLoading(false);
      }
    },
    [filters, toast]
  );

  useEffect(() => {
    fetchBusinesses();
  }, [fetchBusinesses]);

  const handleFilterChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    setFilters((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchBusinesses(1);
  };

  const renderPagination = () => {
    if (!pagination || pagination.total <= pagination.per_page) return null;

    const pageNumbers = [];
    for (let i = 1; i <= pagination.last_page; i++) {
      pageNumbers.push(
        <Button
          key={i}
          onClick={() => fetchBusinesses(i)}
          disabled={loading || i === pagination.current_page}
          variant={i === pagination.current_page ? "primary" : "outline"}
          className="mx-1"
        >
          {i}
        </Button>
      );
    }
    return <div className="mt-6 flex justify-center gap-2">{pageNumbers}</div>;
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case "approved":
        return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
      case "rejected":
        return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400";
      default:
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400";
    }
  };

  return (
    <div className="p-4">
      <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
          Business & Skills Directory
        </h1>
        <Link to="/user/business/create">
          <Button variant="primary" className="mt-3 sm:mt-0">
            Create New Business Listing
          </Button>
        </Link>
      </div>

      <Card extra="w-full p-4 mb-4">
        <form
          onSubmit={handleSearch}
          className="grid grid-cols-1 gap-3 sm:grid-cols-4"
        >
          <input
            type="text"
            name="search"
            placeholder="Search by name or description..."
            value={filters.search}
            onChange={handleFilterChange}
            className="input sm:col-span-2"
          />
          <select
            name="village"
            value={filters.village}
            onChange={handleFilterChange}
            className="input"
          >
            <option value="">All Villages</option>
            <option value="Ehihie">Ehihie</option>
            <option value="Eziama">Eziama</option>
            <option value="Umuezumoha">Umuezumoha</option>
            <option value="Avuvu">Avuvu</option>
          </select>
          <Button type="submit" disabled={loading} variant="primary">
            {loading ? "Searching..." : "Search"}
          </Button>
        </form>
      </Card>

      {loading && businesses.length === 0 ? (
        <Card extra="w-full p-4">
          <p>Loading businesses...</p>
        </Card>
      ) : businesses.length > 0 ? (
        <>
          <div className="overflow-x-auto rounded bg-white shadow dark:bg-navy-700 dark:shadow-none">
            <table className="min-w-full">
              <thead>
                <tr className="border-b bg-gray-50 dark:bg-gray-900/50">
                  <th className="p-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Name
                  </th>
                  <th className="p-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Category
                  </th>
                  <th className="p-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Owner
                  </th>
                  <th className="p-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Village
                  </th>
                  <th className="p-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Status
                  </th>
                  <th className="p-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {businesses.map((business) => (
                  <tr
                    key={business.id}
                    className="border-t hover:bg-gray-50 dark:hover:bg-gray-900/30"
                  >
                    <td className="p-3 text-sm font-medium text-gray-800 dark:text-white">
                      {business.name}
                    </td>
                    <td className="p-3 text-sm text-gray-600 dark:text-gray-400">
                      {business.category}
                    </td>
                    <td className="p-3 text-sm text-gray-600 dark:text-gray-400">
                      {business.user.firstname} {business.user.lastname}
                    </td>
                    <td className="p-3 text-sm text-gray-600 dark:text-gray-400">
                      {business.user.village}
                    </td>
                    <td className="p-3">
                      <span
                        className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${getStatusBadgeColor(
                          business.status
                        )}`}
                      >
                        {business.status.charAt(0).toUpperCase() +
                          business.status.slice(1)}
                      </span>
                    </td>
                    <td className="p-3">
                      <Link to={`/user/businesses/${business.id}`}>
                        <Button size="sm" variant="outline">
                          View Details
                        </Button>
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {renderPagination()}
        </>
      ) : (
        <Card extra="w-full p-4">
          <p className="text-gray-500 dark:text-gray-400">
            No businesses found.
          </p>
        </Card>
      )}
    </div>
  );
};

export default BusinessDirectory;
